import { useEffect, useState } from "react";
import { motion, AnimatePresence, animate, useReducedMotion } from "framer-motion";
import {
  LayoutDashboard, ShieldCheck, Landmark, Receipt, FolderLock, Network, AlertTriangle, Clock, ChevronDown, Search, Bell, CheckCircle2, Loader2,
} from "lucide-react";
import { AQMark } from "@/components/app/Logo";
import { HealthGauge } from "@/components/app/HealthGauge";

const NAV = [LayoutDashboard, ShieldCheck, Landmark, Receipt, FolderLock, Network];

const ALERTS = [
  ["Ontario Annual Return", "12 days overdue", "bg-red-500", "text-red-300"],
  ["BC Annual Report", "Due in 6 days", "bg-amber-400", "text-amber-300"],
  ["Delaware Franchise Tax", "Missing filing", "bg-orange-500", "text-orange-300"],
];

const TAXES = [
  ["GST/HST Q3", "Oct 31", "9d", "text-amber-300"],
  ["T2 Corporate Return", "Nov 30", "39d", "text-slate-300"],
  ["Payroll Remittance", "Nov 15", "24d", "text-slate-300"],
];

const RECEIPTS = [["Staples", "$214.60"], ["Air Canada", "$1,148.00"], ["AWS", "$389.12"]];

const CORPS = [
  ["Northbridge Holdings Inc.", "Federal", 91, 91],
  ["Brightline Cleaning Ltd.", "Ontario", 76, 95],
  ["Cedar Peak Ventures LLC", "Delaware", 58, 58],
  ["Harbourview Hospitality", "BC", 22, 22],
];

const tone = (s) => (s >= 75 ? "#10B981" : s >= 50 ? "#F59E0B" : "#EF4444");

// idle -> resolving -> resolved -> (loop)
const TIMELINE = [["idle", 2600], ["resolving", 1500], ["resolved", 5200]];

const useDemoPhase = () => {
  const reduce = useReducedMotion();
  const [phase, setPhase] = useState("idle");
  useEffect(() => {
    if (reduce) { return undefined; }
    const i = TIMELINE.findIndex(([p]) => p === phase);
    const t = setTimeout(() => setPhase(TIMELINE[(i + 1) % TIMELINE.length][0]), TIMELINE[i][1]);
    return () => clearTimeout(t);
  }, [phase, reduce]);
  return phase;
};

const useCount = (target) => {
  const [v, setV] = useState(target);
  useEffect(() => {
    if (target < v) { setV(target); return undefined; }
    const c = animate(v, target, { duration: 1.4, ease: [0.2, 0.7, 0.2, 1], onUpdate: (x) => setV(Math.round(x)) });
    return () => c.stop();
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  return v;
};

const Panel = ({ title, icon: Icon, badge, children, className = "", testId }) => (
  <div className={`rounded-xl border border-white/[0.07] bg-white/[0.025] p-3.5 ${className}`} data-testid={testId}>
    <div className="flex items-center justify-between mb-2.5">
      <span className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-200"><Icon className="h-3.5 w-3.5 text-emerald-400" />{title}</span>
      {badge && <span className="text-[10px] font-mono rounded-md bg-white/[0.06] px-1.5 py-0.5 text-slate-300 transition-colors duration-500" data-testid={`${testId}-badge`}>{badge}</span>}
    </div>
    {children}
  </div>
);

const Row = ({ children, className = "" }) => <div className={`flex items-center justify-between gap-2 py-1.5 border-t border-white/[0.05] first:border-t-0 text-[11px] ${className}`}>{children}</div>;

const Sidebar = () => (
  <div className="hidden sm:flex w-12 shrink-0 flex-col items-center gap-3 border-r border-white/[0.06] bg-[#0A0E1A] py-3.5">
    <AQMark size={26} testId="hero-shot-mark" />
    <div className="mt-2 flex flex-col gap-2">
      {NAV.map((I, i) => (
        <span key={i} className={`grid h-8 w-8 place-items-center rounded-lg ${i === 0 ? "bg-emerald-500/15 text-emerald-400" : "text-slate-500"}`}><I className="h-4 w-4" /></span>
      ))}
    </div>
  </div>
);

const TopBar = ({ done }) => (
  <div className="flex items-center gap-3 border-b border-white/[0.06] px-4 py-2.5">
    <span className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[11px] text-slate-200" data-testid="hero-shot-entity-switcher">
      All corporations · 4 <ChevronDown className="h-3 w-3 text-slate-400" />
    </span>
    <span className="hidden md:flex flex-1 items-center gap-1.5 rounded-lg bg-white/[0.03] px-2.5 py-1 text-[11px] text-slate-500"><Search className="h-3 w-3" />Ask AccruallQ…</span>
    <span className="ml-auto relative"><Bell className="h-4 w-4 text-slate-400" /><span className={`absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full transition-colors duration-500 ${done ? "bg-emerald-400" : "bg-red-500"}`} /></span>
    <span className="grid h-6 w-6 place-items-center rounded-full bg-emerald-500/20 text-[10px] font-semibold text-emerald-300">JM</span>
  </div>
);

const ScorePanel = ({ done }) => {
  const score = useCount(done ? 91 : 82);
  const compliance = useCount(done ? 97 : 88);
  return (
    <Panel title="Corporate Health Score" icon={ShieldCheck} badge={done ? "▲ 9 just now" : "▲ 4 this week"} className="sm:row-span-2 flex flex-col" testId="hero-shot-score">
      <div className="flex flex-1 flex-col items-center justify-center">
        <div className={`rounded-full transition-shadow duration-700 ${done ? "shadow-[0_0_60px_-10px_rgba(16,185,129,0.55)]" : ""}`}>
          <HealthGauge score={done ? 91 : 82} display={score} size={150} stroke={12} label="Group health" testId="hero-gauge" />
        </div>
        <div className="mt-3 grid w-full grid-cols-2 gap-1.5">
          {[["Compliance", compliance], ["Tax", 71], ["Governance", 90], ["Receipts", 78]].map(([l, v]) => (
            <div key={l} className="rounded-md bg-white/[0.03] px-2 py-1 text-[10px] text-slate-400 flex justify-between">
              <span>{l}</span><span className={`font-mono ${l === "Compliance" && done ? "text-emerald-300" : "text-slate-200"}`} data-testid={`hero-shot-sub-${l.toLowerCase()}`}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
};

const AlertStatus = ({ phase, fallback, tc }) => {
  if (phase === "resolving") { return <span className="flex items-center gap-1 text-emerald-300"><Loader2 className="h-3 w-3 animate-spin" />Filing…</span>; }
  if (phase === "resolved") { return <motion.span initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex items-center gap-1 text-emerald-400"><CheckCircle2 className="h-3 w-3" />Resolved</motion.span>; }
  return <span className={tc}>{fallback}</span>;
};

const AlertsPanel = ({ phase }) => (
  <Panel title="Compliance Alerts" icon={AlertTriangle} badge={phase === "resolved" ? "2 open" : "3 open"} testId="hero-shot-alerts">
    {ALERTS.map(([t, s, dot, tc], i) => {
      const p = i === 0 ? phase : "idle";
      return (
        <Row key={t} className={`rounded-md transition-colors duration-500 ${p === "resolving" ? "bg-emerald-500/10 -mx-1.5 px-1.5" : ""}`}>
          <span className={`flex items-center gap-2 truncate transition-colors duration-500 ${p === "resolved" ? "text-slate-500 line-through" : "text-slate-200"}`} data-testid={i === 0 ? "hero-shot-alert-target" : undefined}>
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full transition-colors duration-500 ${p === "idle" ? dot : "bg-emerald-400"}`} />{t}
          </span>
          <span className="shrink-0" data-testid={i === 0 ? "hero-shot-alert-status" : undefined}><AlertStatus phase={p} fallback={s} tc={tc} /></span>
        </Row>
      );
    })}
  </Panel>
);

const TaxPanel = () => (
  <Panel title="Tax Deadlines" icon={Clock} badge="Next 60 days" testId="hero-shot-taxes">
    {TAXES.map(([t, d, n, c]) => (
      <Row key={t}><span className="text-slate-200 truncate">{t}</span><span className="shrink-0 font-mono text-slate-500">{d} <span className={c}>· {n}</span></span></Row>
    ))}
  </Panel>
);

const ReceiptsPanel = () => (
  <Panel title="Uncategorized Receipts" icon={Receipt} badge="3 to review" className="flex flex-col" testId="hero-shot-receipts">
    {RECEIPTS.map(([v, a]) => (
      <Row key={v}><span className="text-slate-200">{v}</span><span className="flex items-center gap-2"><span className="font-mono text-slate-300">{a}</span><span className="rounded bg-blue-500/15 px-1.5 text-[10px] text-blue-300">Review</span></span></Row>
    ))}
    <div className="mt-auto pt-3">
      <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1"><span>Categorized this month</span><span className="font-mono text-slate-300">41 / 44</span></div>
      <div className="h-1 rounded-full bg-white/10 overflow-hidden"><div className="h-full w-[93%] rounded-full bg-emerald-500" /></div>
      <div className="mt-2.5 rounded-lg bg-emerald-500/15 py-1.5 text-center text-[11px] font-semibold text-emerald-300">Auto-categorize with AI</div>
    </div>
  </Panel>
);

const CorpRow = ({ name, juris, from, to, done }) => {
  const s = useCount(done ? to : from);
  const c = tone(s);
  return (
    <Row>
      <span className="min-w-0"><span className="block truncate text-slate-200">{name}</span><span className="text-[10px] text-slate-500">{juris}</span></span>
      <span className="flex items-center gap-2 shrink-0">
        <span className="h-1 w-12 rounded-full bg-white/10 overflow-hidden"><span className="block h-full rounded-full transition-[width] duration-1000" style={{ width: `${s}%`, background: c }} /></span>
        <span className="font-mono w-5 text-right" style={{ color: c }}>{s}</span>
      </span>
    </Row>
  );
};

const CorpsPanel = ({ done }) => (
  <Panel title="Your Corporations" icon={Network} badge="4 entities" testId="hero-shot-corporations">
    {CORPS.map(([n, j, from, to]) => <CorpRow key={n} name={n} juris={j} from={from} to={to} done={done} />)}
  </Panel>
);

const Toast = ({ show }) => (
  <AnimatePresence>
    {show && (
      <motion.div initial={{ opacity: 0, y: 16, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.4 }}
        className="absolute bottom-3 right-3 left-3 sm:left-auto z-10 flex items-center gap-2.5 rounded-xl border border-emerald-500/30 bg-[#0A1A17]/95 px-3.5 py-2.5 shadow-2xl backdrop-blur-xl"
        data-testid="hero-shot-toast">
        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
        <span className="min-w-0 text-[11px] leading-tight text-slate-200">
          <span className="font-semibold">Ontario Annual Return filed</span>
          <span className="block text-emerald-300">Corporate Health 82 → 91 · Excellent</span>
        </span>
      </motion.div>
    )}
  </AnimatePresence>
);

export const HeroScreenshot = () => {
  const phase = useDemoPhase();
  const done = phase === "resolved";
  return (
    <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }}
      className="relative w-full" data-testid="hero-screenshot" data-phase={phase}>
      <div className="absolute inset-0 sm:-inset-6 rounded-[32px] bg-emerald-500/10 blur-3xl" aria-hidden />
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0D1322] shadow-[0_40px_120px_-30px_rgba(16,185,129,0.35)] ring-1 ring-white/5">
        <div className="flex items-center gap-2 border-b border-white/[0.06] bg-[#0A0E1A] px-4 py-2.5">
          {["bg-red-400/70", "bg-amber-400/70", "bg-emerald-400/70"].map((c) => <span key={c} className={`h-2.5 w-2.5 rounded-full ${c}`} />)}
          <span className="mx-auto rounded-md bg-white/[0.04] px-3 py-0.5 text-[10px] font-mono text-slate-500">app.accruallq.com/dashboard</span>
        </div>
        <div className="flex">
          <Sidebar />
          <div className="min-w-0 flex-1">
            <TopBar done={done} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 p-3">
              <ScorePanel done={done} />
              <AlertsPanel phase={phase} />
              <TaxPanel />
              <ReceiptsPanel />
              <CorpsPanel done={done} />
            </div>
          </div>
        </div>
        <Toast show={done} />
      </div>
    </motion.div>
  );
};
